import { useContext } from 'react';
import * as Sentry from '@sentry/react';

import { DemoLeadValues } from '@/components/DemoLead/DemoLeadModal';
import { saveDemoLead } from '@/components/DemoLead/demoLeadStorage';
import config from '@/config';
import { PersistContext } from '@/context/persistContext';
import { useLogout } from '@/hooks/patient.hooks';
import { useRequestDemo } from '@/hooks/useRequestDemo';

/**
 * Launches (or relaunches) the website demo for the given lead: creates a
 * DemoRequest, wipes any previous session state, persists the lead for the
 * new session and reloads the app on the demo-practice entry URL with the
 * chosen variant.
 *
 * Rejects — before touching any session state — when the DemoRequest cannot
 * be created; the caller is expected to surface a retry.
 */
export const useLaunchDemo = () => {
  const persistor = useContext(PersistContext);
  const { mutateAsync: logout } = useLogout();
  const { mutateAsync: requestDemo } = useRequestDemo();

  return async (values: DemoLeadValues, existingDemoRequestId?: string) => {
    let demoRequestId = existingDemoRequestId;
    if (!demoRequestId) {
      try {
        const demoRequest = await requestDemo({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          speciality: values.speciality,
          referrer: 'site-demo',
          variant: values.variantSlug,
        });
        if (!demoRequest?.id) {
          throw new Error('POST /demoRequest returned no id');
        }
        demoRequestId = demoRequest.id;
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'website-demo-lead' },
          fingerprint: ['demo-lead-creation-failed'],
          extra: {
            speciality: values.speciality,
            variantSlug: values.variantSlug,
          },
        });
        throw error;
      }
    }
    try {
      // Clears a stale temporary-patient cookie from a previous session
      await logout();
    } catch {
      // Logout may fail (no active session) — cleanup must still proceed
    }
    persistor?.pause();
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage unavailable (private mode, cookies blocked) — reading it
      // throws before it can be wiped. The redirect below still has to
      // happen: a stale session is recoverable, a dead button is not
    }
    saveDemoLead({ ...values, demoRequestId });
    window.location.href = `/${
      config.demoPracticeSlug
    }?ref=site&demo_lead=${encodeURIComponent(demoRequestId)}&variant=${
      values.variantSlug
    }`;
  };
};
